import { AdPlacement } from '@api/api';
import { AssetChart } from '@components/article/AssetChart/AssetChart';
import { RelatedArticles } from '@components/article/RelatedArticles';
import { ICardViewOptions } from '@components/article/RelatedArticles/RelatedArticles.types';
import { TweetWidget } from '@components/article/TweetWidget';
import { PageLink } from '@components/common/PageLink';
import {
  DashedContainer,
  LI,
  OL,
  OverviewContainer,
  PacItem,
  ProsAndCons,
  UL,
} from '@components/common/WYSIWYG/RenderHTMLText.styles';
import ConsIcon from '@components/icons/cons.svg';
import ProsIcon from '@components/icons/pros.svg';
import { FormatType } from '@consts/wysiwyg';
import { useProxyApiCall } from '@queries/useProxyApi';
import {
  Blockquote,
  Figcaption,
  Figure,
  Heading,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Link,
  Overview,
  P,
  TableBase,
  Td,
  Th,
} from '@student-coin/coinpaper-components';
import { createIdFromContent } from '@utils/createIdFromContent';
import { getImageHashFromUrl } from '@utils/renderToHTML';
import dynamic from 'next/dist/shared/lib/dynamic';
import { Fragment, ReactElement } from 'react';
import { Node, Text } from 'slate';
import { AdContainer } from '../AdContainer/AdContainer';
import { ImageWithLightBox } from '../Image';
import { NodeProps } from './renderToHTML.types';
import { escapeExternalUrl, escapeInternalUrl } from './renderToHTML.utils';

const AdPlacementComponent = dynamic(
  () => import('@components/common/AdPlacement').then((mod) => mod.AdPlacement),
  { ssr: false },
);

const desktopCardView: ICardViewOptions = {
  thumbnail: 'left',
  variant: 'medium',
  size: 'mediumXx',
};

const mobileCardView: ICardViewOptions = { thumbnail: 'left', variant: 'small' };

// Corresponds to Leaves from WYSIWYG
const formatTextNode = (node: NodeProps, index: number) => {
  let formattedText = node.text as string | ReactElement;

  if (node.bold) {
    formattedText = <strong key={index}>{formattedText}</strong>;
  }

  if (node.italic) {
    formattedText = <em key={index}>{formattedText}</em>;
  }

  if (node.underline) {
    formattedText = <u key={index}>{formattedText}</u>;
  }

  if (node.strike) {
    formattedText = <s key={index}>{formattedText}</s>;
  }

  return formattedText;
};

// Corresponds to Elements from WYSIWYG
const formatBlockNode = (node: NodeProps, children, index: number, ads: AdPlacement[]) => {
  switch (node.type) {
    case FormatType.unorderedList:
      return <UL key={index}>{children}</UL>;
    case FormatType.listItem:
      return <LI key={index}>{children}</LI>;
    case FormatType.orderedList:
      return <OL key={index}>{children}</OL>;
    case FormatType.link:
      return (
        <Link
          key={index}
          href={escapeExternalUrl(node.url)}
          rel={`${node.followed ? '' : 'nofollow '}noopener`}
          target="_blank"
          variant="external"
        >
          {children}
        </Link>
      );
    case FormatType.linkInternal:
      return (
        <PageLink key={index} href={escapeInternalUrl(node.url)}>
          <Link key={index} variant="internal" target="_blank" showArrow>
            {children}
          </Link>
        </PageLink>
      );
    case FormatType.paragraph:
      return (
        <P key={index} variant="large">
          {children}
        </P>
      );
    case FormatType.image:
      return (
        <ImageWithLightBox
          key={index}
          /* TODO: Add hash to images in article's content and remove getImageHashFromUrl function */
          url={getImageHashFromUrl(node.url)}
          fullUrl={node.url}
          alternativeText={node.alternativeText}
          caption={node.caption}
          calculateWidth
          objectFit="contain"
          containerAs="figure"
          originalSize
        />
      );
    case FormatType.heading:
      return children.length === 1 && children[0] === '' ? null : (
        <Heading key={index} id={createIdFromContent(children[0])}>
          {children}
        </Heading>
      );
    case FormatType.heading3:
      return <Heading3 key={index}>{children}</Heading3>;
    case FormatType.heading4:
      return <Heading4 key={index}>{children}</Heading4>;
    case FormatType.heading5:
      return <Heading5 key={index}>{children}</Heading5>;
    case FormatType.heading6:
      return <Heading6 key={index}>{children}</Heading6>;
    case FormatType.figure:
      return <Figure key={index}>{children}</Figure>;
    case FormatType.blockquote:
      return <Blockquote key={index}>{children}</Blockquote>;
    case FormatType.figcaption:
      return <Figcaption key={index}>{children}</Figcaption>;
    case FormatType.relatedArticle:
      return node.relatedArticles ? (
        <DashedContainer key={index}>
          <RelatedArticles
            border="none"
            items={node.relatedArticles}
            desktopCardView={desktopCardView}
            mobileCardView={mobileCardView}
            noFooter
          />
        </DashedContainer>
      ) : null;
    case FormatType.table:
      return <TableBase key={index}>{children}</TableBase>;
    case FormatType.tableRow:
      return <tr key={index}>{children}</tr>;
    case FormatType.tableCell:
      return <Td key={index}>{children}</Td>;
    case FormatType.tableHeader:
      return (
        <Th key={index} scope={node.scope}>
          {children}
        </Th>
      );
    case FormatType.tweet:
      return <TweetWidget tweetId={node.tweetId} key={index} />;
    case FormatType.assetChart:
      return <AssetChart key={index} assetTicker={node.assetTicker} />;
    case FormatType.ad:
      return (
        <AdContainer adPlacements={ads} placement={node.adPlacement} key={node.adId}>
          <AdPlacementComponent adPlacements={ads} placement={node.adPlacement} id={node.adId} />
        </AdContainer>
      );
    case FormatType.htmlCode:
      if (children[0].includes('iframe')) {
        const src = children[0].match(/src="([^"]*)"/)[1] || '';
        const width = children[0].match(/width="([^"]*)"/)[1] || '';
        const height = children[0].match(/height="([^"]*)"/)[1] || '';

        return (
          <iframe
            key={index}
            src={src}
            width={width}
            height={height}
            frameBorder="0"
            title="iframe"
          />
        );
      }
      return <div key={index} dangerouslySetInnerHTML={{ __html: children[0] }} />;
    case FormatType.prosAndCons:
      return <ProsAndCons key={index}>{children}</ProsAndCons>;
    case FormatType.pacContainer:
      return <div key={index}>{children}</div>;
    case FormatType.pros:
      return (
        <PacItem key={index}>
          <ProsIcon />
          <span>{children}</span>
        </PacItem>
      );
    case FormatType.cons:
      return (
        <PacItem key={index}>
          <ConsIcon />
          <span>{children}</span>
        </PacItem>
      );
    case FormatType.description:
      return (
        <OverviewContainer key={index}>
          <Overview
            heading={node.title}
            url={node.buttonUrl}
            logoUrl={node.image}
            about={node.about}
            details={node.details}
            pros={node.pros}
            cons={node.cons}
            buttonLabel={node.buttonLabel}
            follow={!!node.follow}
          />
        </OverviewContainer>
      );
    default:
      return node?.url ? (
        <P key={index}>{children}</P>
      ) : (
        <Fragment key={index}>{children}</Fragment>
      );
  }
};

const ExternalApiData = ({ node, api, accessor, path, index }) => {
  const proxyApiCallResult = useProxyApiCall({ api, accessor, path });

  return <>{formatTextNode({ ...node, text: proxyApiCallResult.data?.value }, index)}</>;
};

export const serialize = (
  node: NodeProps | Node,
  ads?: AdPlacement[],
  index?: number,
): string | ReactElement => {
  if ((node as NodeProps).apiCallMetadata) {
    return (
      <ExternalApiData
        node={node}
        index={index}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        api={node.apiCallMetadata.api}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        path={node.apiCallMetadata.path}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        accessor={node.apiCallMetadata.accessor}
      />
    );
  }

  if (Text.isText(node)) {
    return formatTextNode(node as NodeProps, index ?? 0);
  }

  const children = node.children?.map((n, i) =>
    serialize(n, ads, i + (index ?? 0) + Math.random()),
  );

  return formatBlockNode(node as NodeProps, children, index, ads);
};
